import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReactApexChart from "react-apexcharts";
import API from "../../../../../api";
import ChangeHistoryOutlinedIcon from "@mui/icons-material/ChangeHistoryOutlined";

// Funzione per amplificare le differenze nei dati
const amplifyDifferences = (dataArray, amplificationFactor = 2) => {
  const avg = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
  return dataArray.map((val) => {
    const diff = val - avg;
    return avg + diff * amplificationFactor;
  });
};

const TriangleChartTooltip = ({ detail, ipSorgente, ipDestinatario, timeStamp }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false); // Stato per il dialog

  useEffect(() => {
    setLoading(true);
    setChartData(null);

    const abortController = new AbortController();
    const { signal } = abortController;

    // Chiamata API per ottenere i dati del grafico
    API.getTiresia(
      {
        log_stat: true,
        dettaglio: detail,
        ipSorgente: ipSorgente,
        ipDestinatario: ipDestinatario,
        timeStamp: timeStamp,
      },
      signal
    )
      .then((res) => {
        setChartData({
          hour: (res.data.anomalies.hour * 100).toFixed(2),
          ipDestinatario: (res.data.anomalies.ipDestinatario * 100).toFixed(2),
          ipSorgente: (res.data.anomalies.ipSorgente * 100).toFixed(2),
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore durante la chiamata API:", err);
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [detail, ipSorgente, ipDestinatario, timeStamp]);

  // Prepara i dati reali e amplificati
  const realData = chartData
    ? [
        parseFloat(chartData.hour),
        parseFloat(chartData.ipDestinatario),
        parseFloat(chartData.ipSorgente),
      ]
    : [];

  const amplifiedData = amplifyDifferences(realData, 5.5); // Amplifica i dati per il posizionamento

  // Configurazione del grafico ApexCharts
  const chartOptions = {
    chart: {
      type: "radar",
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      radar: {
        size: 100,
        polygons: {
          strokeColors: "#d3d3d3",
          fill: {
            colors: ["#2c2c2c", "#3c3c3c"],
          },
        },
      },
    },
    colors: ["#459296"],
    markers: {
      size: 5,
      colors: ["#A8A8A8"],
      strokeColor: "#8a2be2",
      strokeWidth: 2,
      hover: {
        size: 8, // Ingrandisce il punto al passaggio del mouse
      },
    },
    xaxis: {
      categories: ["Hour", "Dest IP", "Source IP"],
      labels: {
        style: {
          fontSize: "14px",
          fontWeight: "bold",
        },
      },
    },
    yaxis: {
      show: false, // Nasconde completamente l'asse Y
    },
    tooltip: {
      y: {
        formatter: function (val, opts) {
          const index = opts.dataPointIndex;
          return `${realData[index]}`;
        },
      },
    },
    legend: {
      labels: {
        colors: "#ffffff",
      },
    },
  };

  const chartSeries = chartData
    ? [
        {
          name: "Anomaly Score",
          data: amplifiedData, // Usa dati amplificati per il posizionamento
        },
      ]
    : [];

  // Gestione apertura/chiusura Dialog
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  return (
    <div>
      {/* Icona cliccabile per aprire il dialog */}
      <IconButton onClick={handleOpenDialog} aria-label="open-chart">
        <ChangeHistoryOutlinedIcon />
      </IconButton>

      {/* Finestra di Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Triangle Anomaly Chart
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
              <CircularProgress />
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              id="apexchart"
            >
              <ReactApexChart
                id="chartGlobal"
                options={chartOptions}
                series={chartSeries}
                type="radar"
                height={300}
                width={600}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TriangleChartTooltip;
